import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X } from 'lucide-react';
import ChatBox from './ChatBox';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/medicare-explained', label: 'Medicare Explained' },
    { path: '/turning-65', label: 'Turning 65' },
    { path: '/annual-review', label: 'Medicare Annual Review' },
    { path: '/chronic-needs', label: 'Chronic Needs Plan' },
    { path: '/individual-family', label: 'Individual & Family' },
    { path: '/dental', label: 'Dental & Vision' },
    { path: '/group-insurance', label: 'Group Insurance' },
    { path: '/medicare-calls', label: 'Medicare Fraud' },
    { path: '/about', label: 'About Us' },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/images/carrier-new-logo.png" 
                alt="LifeLine Health Plans" 
                className="h-12"
              />
              <span className="text-xl font-bold text-teal-600 hidden sm:inline">LifeLine Health Plans</span>
            </Link>
            
            {/* Desktop Phone */}
            <div className="hidden md:flex items-center gap-4">
              <a href="tel:734.209.6712" className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold">
                <Phone size={20} />
                734.209.6712
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:block border-t border-gray-200">
            <div className="flex flex-wrap justify-center py-2 gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === item.path
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-black bg-opacity-50 z-40" onClick={() => setMobileMenuOpen(false)}>
            <div className="bg-white w-64 h-full shadow-xl" onClick={(e) => e.stopPropagation()}>
              <nav className="py-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={`block px-6 py-3 text-base font-medium transition-colors ${
                      location.pathname === item.path
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="px-6 py-4 border-t border-gray-200">
                <a href="tel:734.209.6712" className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold">
                  <Phone size={20} />
                  734.209.6712
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-bold mb-4 text-teal-400">Contact Us</h3>
              <div className="space-y-2">
                <p className="flex items-center gap-2">
                  <Phone size={16} />
                  <a href="tel:734.209.6712" className="hover:text-teal-400 transition-colors">734.209.6712</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail size={16} />
                  <a href="mailto:Paulette@llplans.com" className="hover:text-teal-400 transition-colors">Paulette@llplans.com</a>
                </p>
                <p className="text-gray-300 text-sm mt-3">
                  28003 Center Oak Court, Ste. 103<br/>
                  Wixom, MI 48393
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-teal-400">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/medicare-explained" className="hover:text-teal-400 transition-colors">Medicare Explained</Link></li>
                <li><Link to="/turning-65" className="hover:text-teal-400 transition-colors">Turning 65</Link></li>
                <li><Link to="/individual-family" className="hover:text-teal-400 transition-colors">Individual & Family</Link></li>
                <li><Link to="/dental" className="hover:text-teal-400 transition-colors">Dental & Vision</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-teal-400">About</h3>
              <p className="text-gray-300">LifeLine Health Plans - Your trusted Medicare and health insurance specialists in Michigan.</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 mt-6">
            <div className="text-sm text-gray-400 space-y-3 max-w-6xl mx-auto leading-relaxed">
              <p>
                We do not offer every plan available in your area. Currently, we represent 8 organizations which offer 75 products in your area. 
                Please contact <a href="https://www.medicare.gov" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline">Medicare.gov</a>, 
                1-800-MEDICARE, or your local State Health Insurance Assistance Program (SHIP) to get information on all of your options.
              </p>
              <p>
                This is a proprietary website and is not associated, endorsed or authorized by the Social Security Administration, 
                the Department of Health and Human Services or the Center for Medicare and Medicaid Services.
              </p>
              <p>
                This site contains decision-support content and information about Medicare, services related to Medicare and services for people with Medicare. 
                If you would like to find more information about the Medicare program please visit the Official U.S. Government Site for Medicare at{' '}
                <a href="https://www.medicare.gov" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline">Medicare.gov</a>
              </p>
            </div>
            <p className="text-center text-gray-500 text-sm mt-6">
              &copy; {new Date().getFullYear()} LifeLine Health Plans. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Box */}
      <ChatBox />
    </div>
  );
};

export default Layout;